/**
 * Carousel Slide 3 — "CTA"
 * Title + date/price + "Se mer på Farmly" + farmly.se badge.
 */

import React from 'react';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import { getPriceCopy } from '@/lib/events/priceCopy';
import type { ShareCardProps } from '../ShareCardBase';
import { FARMLY_COLORS, FARMLY_FONTS } from '../ShareCardBase';

export function CarouselSlide3({ target, spec }: ShareCardProps) {
  const { w, h } = spec;

  let dateLine = '';
  let priceLine = '';
  if (target.type === 'activity') {
    if (target.startDate) {
      try {
        dateLine = format(new Date(target.startDate), "d MMMM yyyy", { locale: sv });
      } catch { /* */ }
    }
    const price = getPriceCopy({ is_free: target.isFree, price_ore: target.priceOre });
    priceLine = price.primary ?? '';
  }

  return (
    <div style={{
      position: 'relative', width: w, height: h, overflow: 'hidden',
      background: `linear-gradient(160deg, ${FARMLY_COLORS.deepForest} 0%, hsl(135, 15%, 28%) 60%, hsl(8, 30%, 30%) 100%)`,
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', textAlign: 'center',
      padding: 64, fontFamily: FARMLY_FONTS.body,
    }}>
      {/* Slide indicator */}
      <div style={{
        position: 'absolute', top: 40, right: 40, zIndex: 3,
        background: 'rgba(255,255,255,0.12)', borderRadius: 16,
        padding: '6px 16px', fontSize: 18, color: FARMLY_COLORS.warmSand,
        fontWeight: 600,
      }}>
        3 / 3
      </div>

      {/* Producer */}
      <div style={{
        fontSize: 16, color: FARMLY_COLORS.warmSand, opacity: 0.6,
        textTransform: 'uppercase', letterSpacing: '0.12em',
        fontWeight: 500, marginBottom: 32,
      }}>
        {target.producerName}
      </div>

      {/* Title */}
      <div style={{
        fontFamily: FARMLY_FONTS.display, fontSize: 44, lineHeight: 1.2,
        color: FARMLY_COLORS.warmSand, maxWidth: w - 128,
        wordWrap: 'break-word', overflowWrap: 'break-word',
      }}>
        {target.title}
      </div>

      {/* Date + price chips */}
      {(dateLine || priceLine) && (
        <div style={{ display: 'flex', gap: 12, marginTop: 28, flexWrap: 'wrap', justifyContent: 'center' }}>
          {dateLine && (
            <div style={{
              background: 'rgba(255,255,255,0.12)', borderRadius: 20,
              padding: '8px 20px', fontSize: 20, color: FARMLY_COLORS.warmSand, fontWeight: 500,
            }}>
              📅 {dateLine}
            </div>
          )}
          {priceLine && (
            <div style={{
              background: 'rgba(255,255,255,0.12)', borderRadius: 20,
              padding: '8px 20px', fontSize: 20, color: FARMLY_COLORS.warmSand, fontWeight: 500,
            }}>
              💰 {priceLine}
            </div>
          )}
        </div>
      )}

      {/* Divider */}
      <div style={{ marginTop: 40, width: 60, height: 3, background: FARMLY_COLORS.accent, borderRadius: 2 }} />

      {/* CTA */}
      <div style={{
        marginTop: 40, fontSize: 26, color: FARMLY_COLORS.warmSand,
        opacity: 0.9, fontWeight: 500,
      }}>
        Se mer på Farmly 👇
      </div>

      {/* Large farmly.se badge */}
      <div style={{
        marginTop: 20,
        fontFamily: FARMLY_FONTS.display, fontSize: 36,
        color: FARMLY_COLORS.warmSand, letterSpacing: '0.02em', opacity: 0.95,
      }}>
        farmly.se
      </div>
    </div>
  );
}
